import { CommonModule, CurrencyPipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  model,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  IAgregarPagoVentaCreditoReq,
  IEmitirCreditoReq,
  IVerDetalleDeudaClienteCreditoRes,
} from '@core/interfaces/request-response/credito.dto.interface';
import { CreditoApiService } from '@core/services/api/credito-api.service';
import { AppService } from '@shared/services/app.service';
import { ToastService } from '@shared/services/toast.service';
import { UiButton, UiDialog, UiTextarea, UiTooltip } from '@shared/components/ui';
import { AgregarPago } from '@shared/components/agregar-pago/agregar-pago';
import { IAgregarPagoComponent } from '@core/interfaces/components/agregarPago.component.interface';
import { TipoPagoApiService } from '@core/services/api/tipo-pago-api.service';
import { TipoPagoService } from '@shared/services/tipo-pago.service';
import { redondearDosDecimales } from '@shared/utils/functions.utils';
import { FormsModule } from '@angular/forms';
import { EmitirComprobante } from '@shared/components/emitir-comprobante/emitir-comprobante';
import { IEmitirComprobanteComponent } from '@core/interfaces/components/emitirComprobante.component.interface';
import { SucursalService } from '@shared/services/sucursal.service';
import { CajaTurnoApiService } from '@core/services/api/caja-turno.service';

@Component({
  selector: 'app-detalle-credito',
  imports: [
    CurrencyPipe,
    UiButton,
    CommonModule,
    AgregarPago,
    UiDialog,
    FormsModule,
    UiTextarea,
    EmitirComprobante,
    UiTooltip,
  ],
  templateUrl: './detalle-credito.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './detalle-credito.css',
})
export class DetalleCredito implements OnInit {
  constructor() {
    effect(() => {
      if (this.sucursalService.sucursalReady() && this.appService.authReady()) {
        this.ventaId = Number(this.activatedRoute.snapshot.paramMap.get('ventaId') || 0);
        if (this.ventaId) {
          this.creditoApiService.verVentaDeudaCliente(this.ventaId).subscribe({
            next: (res) => {
              this.setData(res);
            },
            error: (err) => {
              if (err.status == 401) return;
              this.toastService.show(
                'top-right',
                'error',
                'Error',
                err.message || 'Error al cargar el detalle del crédito',
              );
            },
          });
          this.cajaTurnoApiService.checkApertura().subscribe({
            next: (res) => {
              this.estadoCaja.set(res);
            },
          });
          this.cargarTiposPago();
        }
      }
    });
  }

  ngOnInit(): void {
    this.appService.setPanelHeader({
      title: 'Detalle de Crédito',
      subtitle: 'Aquí puedes ver el detalle del crédito seleccionado',
      showBackButton: true,
    });
  }
  // servicios
  private appService = inject(AppService);
  private activatedRoute = inject(ActivatedRoute);
  private creditoApiService = inject(CreditoApiService);
  private toastService = inject(ToastService);
  private sucursalService = inject(SucursalService);
  private cajaTurnoApiService = inject(CajaTurnoApiService);

  private tipoPagoApiService = inject(TipoPagoApiService);
  private tipoPagoService = inject(TipoPagoService);
  private router = inject(Router);
  // datos
  ventaId = 0;
  data = signal<IVerDetalleDeudaClienteCreditoRes | null>(null);
  estadoCaja = signal<boolean>(false);

  totalPagado = computed(() => {
    const pagos = this.data()?.pagos ?? [];
    return redondearDosDecimales(pagos.reduce((sum, pago) => sum + Number(pago.monto), 0));
  });

  saldoPendiente = computed(() => {
    const total = Number(this.data()?.total ?? 0);
    return redondearDosDecimales(total - this.totalPagado());
  });

  porcentajeProgreso = computed(() => {
    const total = Number(this.data()?.total ?? 0);
    return redondearDosDecimales(total === 0 ? 0 : (this.totalPagado() / total) * 100);
  });

  modal_agregar_pago = model<boolean>(false);
  modal_emitir_venta = model<boolean>(false);

  observaciones_pago: string = '';
  // métodos
  agregarPago(data: IAgregarPagoComponent) {
    if (!this.ventaId) return;
    if (!data || !data.tipo_pago) {
      this.toastService.show('top-right', 'error', 'Error', 'Tipo de pago inválido');
      return;
    }
    if (data.monto <= 0) {
      this.toastService.show('top-right', 'error', 'Error', 'El monto debe ser mayor a cero');
      return;
    }
    if (data.monto > this.saldoPendiente()) {
      this.toastService.show('top-right', 'error', 'Error', 'El monto excede el saldo pendiente');
      return;
    }
    const req: IAgregarPagoVentaCreditoReq = {
      venta_id: this.ventaId,
      tipo_pago_nombre: data.tipo_pago.nombre,
      tipo_pago_id: data.tipo_pago.id,
      tipo_transaccion: data.tipo_pago.tipo_transaccion,
      monto: data.monto,
    };
    this.creditoApiService.agregarPago(req).subscribe({
      next: (res) => {
        this.toastService.show('top-right', 'success', 'Éxito', 'Pago agregado correctamente');
        // actualizar datos
        this.setData(res);
        // cerrar modal
        this.modal_agregar_pago.set(false);
      },
      error: (err) => {
        if (err.status == 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error.message || 'Error al agregar el pago',
        );
      },
    });
  }
  actualizarObservaciones() {
    if (!this.ventaId) return;
    this.creditoApiService
      .actualizarObservacionesPagoCredito(this.ventaId, this.observaciones_pago)
      .subscribe({
        next: () => {
          this.toastService.show(
            'top-right',
            'success',
            'Éxito',
            'Observaciones actualizadas correctamente',
          );
          this.setData({
            ...this.data()!,
            observacion: this.observaciones_pago,
          });
        },
        error: (err) => {
          if (err.status == 401) return;
          this.toastService.show(
            'top-right',
            'error',
            'Error',
            err.error.message || 'Error al actualizar las observaciones',
          );
        },
      });
  }
  emitir(data: IEmitirComprobanteComponent) {
    this.modal_emitir_venta.set(false);
    const req: IEmitirCreditoReq = {
      venta_id: this.ventaId,
      tipo_documento_sunat: data.tipo_documento,
    };
    this.creditoApiService.emitirCredito(req).subscribe({
      next: (res) => {
        this.toastService.show(
          'top-right',
          'success',
          'Éxito',
          res.message || 'Comprobante emitido correctamente',
        );
        this.router.navigate(['/panel/gestion/creditos/ver', this.data()?.cliente.id || 0]);
      },
      error: (err) => {
        if (err.status == 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error.message || 'Error al emitir el comprobante',
        );
      },
    });
  }
  // metodos privados
  private setData(data: IVerDetalleDeudaClienteCreditoRes) {
    this.data.set(data);
    this.observaciones_pago = data.observacion || '';
  }
  private cargarTiposPago() {
    this.tipoPagoApiService.cargarActivos().subscribe((response) => {
      this.tipoPagoService.setTiposPago(response);
    });
  }
}
